import { useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Pagination,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddPhotoAlternateRoundedIcon from "@mui/icons-material/AddPhotoAlternateRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import parse from "html-react-parser";
import MDButton from "components/MDButton";
import {
  askAIChatCall,
  getAIMessagesByThreadCall,
  getAllAIThreadsCall,
} from "../../../redux/api/ai.api";
import { STATIC_COPILOT_COSTS, getCopilotCreditsCall } from "../../../redux/api/copilot.api";
import CreditsBar from "./CreditsBar";
import InsufficientCreditsModal from "./InsufficientCreditsModal";

/*
  ─── AskCopilotChatDialog ────────────────────────────────────
  Web equivalent of the mobile AskAIScreen + AskQuestionScreen
  combined into a single side-by-side dialog. The left rail lists
  previous threads (so it doubles as the "AI History" view) and
  the right pane is a live composer/transcript.

  Backend:
    • POST   /api/ai                          (FormData: body, type=ai, entity_type=aiQuestion, thread_id?, title?, file?)
    • GET    /api/ai/my                       (date-bucketed thread list)
    • GET    /api/ai/:threadId/messages       (history for a thread)
    • GET    /api/lecture-lab/credits         (shared credit balance)

  Credits:
    • Text-only questions are FREE on the server today.
    • Each attached image costs `costs.ai_question_image` (default 2).
    • The composer shows a live cost preview and pre-flights the
      balance before submitting.

  Why we reuse the existing `askAIChatCall` helper instead of
  creating a new one: it already handles the multipart shape and
  upload progress reporting (axios), so the only addition we need
  on the chat side is image attachment + cost preview.
*/

const PAGE_SIZE = 8;

function renderAiHtml(html) {
  return (
    <Box
      sx={{
        fontSize: 15,
        lineHeight: 1.6,
        "& p": { margin: 0, mb: 1 },
        "& p:last-of-type": { mb: 0 },
        "& ul, & ol": { margin: "6px 0 0", pl: 2.5 },
        "& li": { marginBottom: 0.5 },
        "& strong, & b": { fontWeight: 700 },
      }}
    >
      {parse(html || "")}
    </Box>
  );
}

function ThreadRow({ thread, active, onClick }) {
  const preview = thread.first_user_message || "No preview";
  const image = thread.picture_url || thread.uploaded_file?.url || "";
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={(theme) => ({
        p: 1,
        borderRadius: 2,
        cursor: "pointer",
        transition: "background-color .15s ease, border-color .15s ease",
        borderColor: active ? theme.palette.primary.main : theme.palette.divider,
        backgroundColor: active ? alpha(theme.palette.primary.main, 0.06) : "transparent",
        "&:hover": {
          borderColor: theme.palette.primary.main,
        },
      })}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        {image ? (
          <Box
            component="img"
            src={image}
            alt=""
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              objectFit: "cover",
              border: "1px solid rgba(148,163,184,.3)",
              flexShrink: 0,
            }}
          />
        ) : (
          <Avatar
            variant="rounded"
            sx={(theme) => ({
              width: 40,
              height: 40,
              bgcolor: alpha(theme.palette.info.main, 0.12),
              color: theme.palette.info.main,
            })}
          >
            <AutoAwesomeRoundedIcon fontSize="small" />
          </Avatar>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">
            {thread.groupLabel || "Recent"}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              fontWeight: active ? 600 : 400,
            }}
          >
            {preview}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function AskCopilotChatDialog({
  open,
  onClose,
  credits,
  onCreditsChange,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [page, setPage] = useState(1);

  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  const [input, setInput] = useState("");
  const [attachment, setAttachment] = useState(null); // { file, previewUrl }
  const [sending, setSending] = useState(false);
  const [insufficient, setInsufficient] = useState(null);
  const fileInputRef = useRef(null);
  const transcriptRef = useRef(null);

  const imageCost = credits?.costs?.ai_question_image ?? STATIC_COPILOT_COSTS.ai_question_image;
  const pendingCost = attachment ? imageCost : 0;

  const refreshThreads = async () => {
    setThreadsLoading(true);
    try {
      const locale = (localStorage.getItem("i18nextLng") || "en").split("-")[0];
      const res = await getAllAIThreadsCall(locale);
      const grouped = res?.threads || {};
      const flat = Object.entries(grouped).flatMap(([groupLabel, items]) =>
        (Array.isArray(items) ? items : []).map((item) => ({
          ...item,
          groupLabel,
        })),
      );
      flat.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setThreads(flat);
    } finally {
      setThreadsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      refreshThreads();
    }
    // Reset transient state when the dialog closes.
    if (!open) {
      setActiveThread(null);
      setMessages([]);
      setInput("");
      setAttachment(null);
    }
  }, [open]);

  useEffect(() => {
    // Auto-scroll to bottom whenever a new message lands.
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const openThread = async (thread) => {
    setActiveThread(thread);
    setMessagesLoading(true);
    try {
      const res = await getAIMessagesByThreadCall(thread.thread_id);
      const mapped = Array.isArray(res)
        ? [...res].reverse().map((m) => ({
            id: m._id,
            sender: m?.user?._id === "ai" ? "ai" : "user",
            text: m?.text || "",
            image: m?.image || m?.file?.url || "",
            createdAt: m?.createdAt,
          }))
        : [];
      setMessages(mapped);
    } finally {
      setMessagesLoading(false);
    }
  };

  const startNewThread = () => {
    setActiveThread(null);
    setMessages([]);
  };

  const pageCount = Math.max(1, Math.ceil(threads.length / PAGE_SIZE));
  const pagedThreads = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return threads.slice(start, start + PAGE_SIZE);
  }, [threads, page]);

  const handlePickImage = () => {
    if (sending) return;
    fileInputRef.current?.click();
  };

  const onFileChosen = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      window.alert("Only image attachments are supported here. For videos, please use the Community.");
      return;
    }
    // Don't bother previewing > 10 MB images — server enforces
    // its own limits but the browser preview is the slow path.
    if (file.size > 10 * 1024 * 1024) {
      window.alert("Image is too large (>10 MB). Please use a smaller image.");
      return;
    }
    setAttachment({ file, previewUrl: URL.createObjectURL(file) });
    e.target.value = "";
  };

  const clearAttachment = () => {
    if (attachment?.previewUrl) URL.revokeObjectURL(attachment.previewUrl);
    setAttachment(null);
  };

  const submit = async () => {
    const body = input.trim();
    if (!body && !attachment) return;
    if (sending) return;

    // Pre-flight credit check (image questions only).
    if (pendingCost > 0 && credits && Number(credits.creditsRemaining) < pendingCost) {
      setInsufficient({ required: pendingCost, remaining: credits.creditsRemaining });
      return;
    }

    setSending(true);
    // Optimistically append user bubble; we strip it again on
    // failure so the user doesn't end up with phantom messages
    // they can't retry through.
    const optimistic = {
      id: `tmp-user-${Date.now()}`,
      sender: "user",
      text: body,
      image: attachment?.previewUrl || "",
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    const previousInput = input;
    const previousAttachment = attachment;
    setInput("");
    setAttachment(null);

    try {
      const formData = new FormData();
      formData.append("body", body || "(image)");
      formData.append("type", "ai");
      formData.append("entity_type", "aiQuestion");
      formData.append("title", "histology");
      if (activeThread?.thread_id) {
        formData.append("thread_id", activeThread.thread_id);
      }
      if (previousAttachment?.file) {
        formData.append("file", previousAttachment.file);
      }

      const res = await askAIChatCall(formData);

      if (res?.status === 402 || res?.code === "INSUFFICIENT_CREDITS") {
        setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
        setInput(previousInput);
        setAttachment(previousAttachment);
        setInsufficient({
          required: res?.credits?.required ?? pendingCost,
          remaining: res?.credits?.creditsRemaining ?? 0,
        });
        return;
      }

      const aiText =
        res?.messages?.[1]?.content ?? res?.messages?.[res?.messages?.length - 1]?.content ?? "";
      if (aiText) {
        setMessages((prev) => [
          ...prev,
          {
            id: `tmp-ai-${Date.now()}`,
            sender: "ai",
            text: aiText,
            image: "",
            createdAt: new Date().toISOString(),
          },
        ]);
      }

      // If this was a brand-new thread we now have a thread_id;
      // pin it so subsequent sends continue the same conversation
      // and refresh the threads list in the background.
      if (res?.thread_id && !activeThread?.thread_id) {
        setActiveThread({ thread_id: res.thread_id, first_user_message: body });
      }
      refreshThreads();

      // Always refresh credits — the server may have deducted on
      // either side (image cost) or just returned the latest
      // balance for parity with mobile.
      const latest = await getCopilotCreditsCall();
      if (latest && typeof onCreditsChange === "function") {
        onCreditsChange(latest);
      }
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setInput(previousInput);
      setAttachment(previousAttachment);
      window.alert("Failed to reach Microlab Copilot. Please retry in a moment.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        fullScreen={fullScreen}
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: fullScreen ? 0 : 3,
            height: fullScreen ? "100%" : "min(86vh, 760px)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle sx={{ pr: 6, py: 1.4 }}>
          <Stack direction="row" spacing={1.4} alignItems="center">
            <Avatar
              sx={(t) => ({
                width: 32,
                height: 32,
                backgroundImage: `linear-gradient(135deg, ${t.palette.info.main}, ${t.palette.primary.main})`,
              })}
            >
              <AutoAwesomeRoundedIcon fontSize="small" />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography fontWeight={700}>Ask Microlab Copilot</Typography>
              <Typography variant="caption" color="text.secondary">
                Histology & embryology Q&A — text is free, images cost {imageCost} credits each.
              </Typography>
            </Box>
            <Box sx={{ minWidth: 280, display: { xs: "none", md: "block" } }}>
              <CreditsBar credits={credits} compact />
            </Box>
            <IconButton onClick={onClose} sx={{ ml: 0.5 }}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, display: "flex", height: "100%" }}>
          {/* ─── Left rail: threads ─────────────────────────── */}
          <Box
            sx={(t) => ({
              width: { xs: 0, md: 280 },
              minWidth: { md: 240 },
              borderRight: { md: `1px solid ${t.palette.divider}` },
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              p: 1.2,
              gap: 1,
            })}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <MDButton size="small" variant="gradient" color="info" onClick={startNewThread} fullWidth>
                <AutoAwesomeRoundedIcon fontSize="small" sx={{ mr: 0.7 }} />
                New chat
              </MDButton>
              <Tooltip arrow title="Refresh">
                <IconButton size="small" onClick={refreshThreads} disabled={threadsLoading}>
                  {threadsLoading ? <CircularProgress size={14} /> : <RefreshRoundedIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5, mb: 0.5 }}>
              <HistoryRoundedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                History
              </Typography>
            </Stack>
            <Box sx={{ flex: 1, overflowY: "auto", pr: 0.3 }}>
              {threads.length === 0 && !threadsLoading ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", py: 3, textAlign: "center" }}>
                  No previous chats yet.
                </Typography>
              ) : (
                <Stack spacing={0.8}>
                  {pagedThreads.map((t) => (
                    <ThreadRow
                      key={t.thread_id}
                      thread={t}
                      active={activeThread?.thread_id === t.thread_id}
                      onClick={() => openThread(t)}
                    />
                  ))}
                </Stack>
              )}
            </Box>
            {pageCount > 1 ? (
              <Pagination
                size="small"
                page={page}
                count={pageCount}
                shape="rounded"
                color="primary"
                onChange={(_, v) => setPage(v)}
              />
            ) : null}
          </Box>

          {/* ─── Right: transcript + composer ───────────────── */}
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            <Box
              ref={transcriptRef}
              sx={(t) => ({
                flex: 1,
                overflowY: "auto",
                p: { xs: 1.4, md: 2 },
                backgroundColor:
                  t.palette.mode === "dark" ? alpha(t.palette.primary.main, 0.02) : "#f8fafc",
              })}
            >
              {messagesLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress size={22} />
                </Box>
              ) : messages.length === 0 ? (
                <Stack spacing={1} sx={{ py: 6, textAlign: "center", alignItems: "center" }}>
                  <Avatar
                    sx={(t) => ({
                      width: 56,
                      height: 56,
                      backgroundImage: `linear-gradient(135deg, ${t.palette.info.main}, ${t.palette.primary.main})`,
                    })}
                  >
                    <AutoAwesomeRoundedIcon />
                  </Avatar>
                  <Typography variant="h6" fontWeight={700}>
                    Ask Microlab Copilot anything
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460 }}>
                    Paste a question, attach a slide, and the Copilot will explain it in plain language.
                    Histology and embryology only — for anything else, the Copilot will redirect you.
                  </Typography>
                </Stack>
              ) : (
                <Stack spacing={1.2}>
                  {messages.map((m) => (
                    <Box
                      key={m.id}
                      sx={{
                        display: "flex",
                        justifyContent: m.sender === "user" ? "flex-end" : "flex-start",
                      }}
                    >
                      <Paper
                        elevation={0}
                        sx={(t) => ({
                          maxWidth: "85%",
                          p: m.sender === "user" ? 1.4 : 1.6,
                          borderRadius: 2.5,
                          color: m.sender === "user" ? "#fff" : t.palette.text.primary,
                          backgroundColor:
                            m.sender === "user"
                              ? t.palette.info.main
                              : t.palette.mode === "dark"
                                ? alpha("#fff", 0.05)
                                : "#fff",
                          border: m.sender === "user" ? "none" : `1px solid ${t.palette.divider}`,
                          boxShadow:
                            m.sender === "user"
                              ? `0 6px 18px ${alpha(t.palette.info.main, 0.28)}`
                              : "0 1px 2px rgba(15,23,42,.06)",
                        })}
                      >
                        {m.image ? (
                          <Box
                            component="img"
                            src={m.image}
                            alt=""
                            sx={{
                              maxWidth: 280,
                              maxHeight: 280,
                              borderRadius: 1.5,
                              display: "block",
                              mb: m.text ? 1 : 0,
                              objectFit: "cover",
                            }}
                          />
                        ) : null}
                        {m.sender === "ai" ? (
                          renderAiHtml(m.text)
                        ) : (
                          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                            {m.text}
                          </Typography>
                        )}
                      </Paper>
                    </Box>
                  ))}
                  {sending ? (
                    <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                      <Paper
                        elevation={0}
                        sx={(t) => ({
                          p: 1.4,
                          borderRadius: 2.5,
                          backgroundColor:
                            t.palette.mode === "dark" ? alpha("#fff", 0.05) : "#fff",
                          border: `1px solid ${t.palette.divider}`,
                        })}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CircularProgress size={14} />
                          <Typography variant="caption" color="text.secondary">
                            Microlab Copilot is thinking…
                          </Typography>
                        </Stack>
                      </Paper>
                    </Box>
                  ) : null}
                </Stack>
              )}
            </Box>

            {/* Composer */}
            <Box
              sx={(t) => ({
                borderTop: `1px solid ${t.palette.divider}`,
                p: 1.2,
                backgroundColor: t.palette.background.paper,
              })}
            >
              {attachment ? (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <Box
                    component="img"
                    src={attachment.previewUrl}
                    alt=""
                    sx={{ width: 48, height: 48, borderRadius: 1, objectFit: "cover", border: "1px solid rgba(148,163,184,.3)" }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary">
                      Attachment
                    </Typography>
                    <Stack direction="row" spacing={0.6} alignItems="center">
                      <ImageOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                      <Typography variant="body2" noWrap>
                        {attachment.file.name}
                      </Typography>
                    </Stack>
                  </Box>
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={`${imageCost} credits`}
                    sx={{ fontWeight: 700, fontSize: 11 }}
                  />
                  <IconButton size="small" onClick={clearAttachment} aria-label="remove attachment">
                    <CloseRoundedIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ) : null}
              <Stack direction="row" spacing={1} alignItems="flex-end">
                <Tooltip arrow title="Attach image">
                  <span>
                    <IconButton onClick={handlePickImage} disabled={sending}>
                      <AddPhotoAlternateRoundedIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={onFileChosen}
                />
                <TextField
                  fullWidth
                  multiline
                  minRows={1}
                  maxRows={5}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type your histology / embryology question…"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      submit();
                    }
                  }}
                  size="small"
                />
                <MDButton
                  variant="gradient"
                  color="info"
                  disabled={sending || (!input.trim() && !attachment)}
                  onClick={submit}
                  sx={{ minWidth: 64 }}
                >
                  {sending ? <CircularProgress size={16} color="inherit" /> : <SendRoundedIcon fontSize="small" />}
                </MDButton>
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5, px: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {pendingCost > 0 ? `This message will cost ${pendingCost} credits.` : "Text-only messages are free."}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  Press Enter to send · Shift+Enter for newline
                </Typography>
              </Stack>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <InsufficientCreditsModal
        open={!!insufficient}
        required={insufficient?.required}
        remaining={insufficient?.remaining}
        onClose={() => setInsufficient(null)}
      />
    </>
  );
}
